/**
 * NEOPINE 产品页增强
 *
 * 1) 头图下拉缩放：产品页首屏占满视口，向下滚动时收缩并出圆角，
 *    把下面的购买区逐渐"推"出来（对齐 Apple 产品页的 hero 处理）。
 *    - 头图上头还压着公告条 + 页头，所以实际高度是「目标高度 − 那一条的高度」，
 *      否则 100svh 的底边会掉到首屏外，看起来就没铺满。那一条的高度直接吃主题
 *      在 body 上维护的 --header-group-height（实测 103px，滚动中稳定）。
 *    - 全程可逆：滚回去头图原样长回来，反复滚动反复播放，不是一次性动画。
 *    - 只写 CSS 变量（--np-hero-height / --np-hero-radius / --np-hero-progress /
 *      --np-hero-offset），具体视觉全部在 assets/neopine-custom.css 里，便于单独调。
 *    - 只作用于 [id$="__pro_hero"]，检测不到目标元素就直接返回。
 *    - prefers-reduced-motion 下保持静态满屏，不做任何形变。
 *
 * 2) Kit 档位联动图集：图集是**一套**（该配色正面 → 该配色俯视 → 设备+电池 → 宣传图 ×3），
 *    档位不改变图集内容，只决定"当前停在哪一张"：
 *      · Plus（含额外电池）→ 跳到「设备 + 电池」那张
 *      · Standard          → 停在设备那张（第 1 张）
 *    ⚠️ 档位判据读的是按钮上的 `data-np-kit-tier`（由 snippets/neopine-kit-label.liquid
 *    经 variant-main-picker.liquid 注入），**不再拿选项文案去猜**：老师在后台把 Kit 选项值
 *    从 `Device only` / `Device + Battery` 改成 `Standard` / `Plus` 之后，原来那句
 *    `/battery/i.test(选项值)` 永远为假（两个新名字里都没有 battery）⇒ 图集再也不跳图。
 *    图集内容由 snippets/neopine-gallery-order.liquid 负责（各配色暂时共用黑色套图）。
 *
 * 两者都在检测不到目标元素时直接返回，主题编辑器里编辑其他模板不受影响。
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. 头图下拉缩放
   * ------------------------------------------------------------------ */

  const HERO_SELECTOR = '[id$="__pro_hero"]';

  /** 收缩幅度：滚过 0.75 个视口高度走完全程 */
  const SHRINK_RATIO = 0.75;
  /** 收到底之后保留的高度（svh） */
  const MIN_HEIGHT_VH = 74;
  /** 收到底之后的圆角（px） */
  const MAX_RADIUS = 20;

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** 缓出，让收缩"起步跟手、收尾轻柔" */
  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function initHeroShrink() {
    const heroSection = document.querySelector(HERO_SELECTOR);
    if (!heroSection) return;

    const surface = heroSection.querySelector('.section');
    if (!surface) return;

    /**
     * 头图上方那一条（公告条 + 页头）到底占多高。
     *
     * 头图自己是 100svh，但它并不是从 y=0 开始的——上面还压着公告条和页头，
     * 所以它 100svh 的底边会掉到首屏外面，看起来就是"没铺满"。
     * CSS 里负责把这一段减掉：height = --np-hero-height − 那一条的高度。
     *
     * 那一条的高度主题已经在 body 上算好了（--header-group-height，实测 103px，
     * 且滚动过程中不变），所以正常情况下这里什么都不用做，直接吃主题的值最准
     * ——它比我们量得早、也在字体加载完之后自己纠正过。只有它缺失（模板没有
     * 页头、或主题脚本没跑）时才写一个兜底值，且绝不覆盖主题的值。
     */
    function ensureOffset() {
      const fromTheme = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height'));

      if (Number.isFinite(fromTheme) && fromTheme > 0) {
        surface.style.removeProperty('--np-hero-offset');
        return;
      }

      let top = 0;
      for (let node = heroSection; node; node = node.offsetParent) top += node.offsetTop;
      surface.style.setProperty('--np-hero-offset', Math.max(0, top).toFixed(1) + 'px');
    }

    ensureOffset();

    if (prefersReducedMotion) {
      surface.style.setProperty('--np-hero-height', '100svh');
      surface.style.setProperty('--np-hero-radius', '0px');
      window.addEventListener('resize', ensureOffset, { passive: true });
      return;
    }

    let ticking = false;

    function update() {
      ticking = false;

      const distance = window.innerHeight * SHRINK_RATIO;
      const raw = distance > 0 ? window.scrollY / distance : 1;
      const progress = easeOut(Math.min(1, Math.max(0, raw)));

      surface.style.setProperty('--np-hero-height', (100 - (100 - MIN_HEIGHT_VH) * progress).toFixed(3) + 'svh');
      surface.style.setProperty('--np-hero-radius', (MAX_RADIUS * progress).toFixed(2) + 'px');
      surface.style.setProperty('--np-hero-progress', progress.toFixed(4));
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    function onResize() {
      ensureOffset();
      requestUpdate();
    }

    // 监听常驻，不做一次性解绑，保证往回滚能原路退回
    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
  }

  /* ------------------------------------------------------------------ *
   * 2. Kit 档位联动图集
   *
   *    为什么不是"调一次 select() 就完事"：
   *    a) 换变体时主题把整个 media-gallery 换掉（media-gallery.js 里的 replaceWith），
   *       新组件的初始化是在之后一两帧才跑完，期间会把起点设回第 1 张，
   *       刚好盖住我们那次调用（这就是"点了不跳图"的直接原因）；
   *    b) 组件刚挂载时图片还没解码、滚动容器尺寸还是 0，主题内部那次 scrollTo
   *       可能压根不产生位移，而它仍然会把内部 current 设成目标值，
   *       所以"看内部状态"判断成功与否不可靠，必须看几何。
   *    因此这里做三件事：
   *      ① 调主题自己的 select()（分页点、计数器、aria 状态会顺带同步）；
   *      ② 立刻用矩形差核对有没有真的停过去，没到位就直接搬滚动条兜底；
   *      ③ 在一段短窗口内持续校正（主题初始化晚于我们也能被纠正回来），
   *         窗口内一旦发现用户自己在图集里滑动/点箭头，立即收手，不跟用户抢。
   * ------------------------------------------------------------------ */

  /**
   * 套图里「设备 + 电池」那张的 alt，例如
   * "…with the SW001 BAT battery pack, Device + Battery kit"。
   *
   * 不能只认旧文案 `device + battery`：后台把选项值改名时，**媒体 alt 不会跟着变**，
   * 但万一哪天 alt 也被改写了，这条就断了。所以同时接受主题自己的 `kit` 判据 ——
   * snippets/neopine-gallery-order.liquid 也是用 `alt contains 'kit'` 认这张图的。
   */
  const BATTERY_SLIDE_RE = /device\s*\+\s*battery|\bkit\b/i;

  /** 落位后持续校正的时长与轮询间隔（毫秒） */
  const SYNC_WINDOW = 2600;
  const SYNC_STEP = 80;

  /** 判定"已经对齐"的容差（px） */
  const ALIGN_TOLERANCE = 4;

  let syncTimer = null;
  let syncDeadline = 0;

  /** 读取某个选项当前选中的值（兼容按钮与下拉两种渲染） */
  function selectedOptionValue(optionNamePattern) {
    for (const input of document.querySelectorAll('input[type="radio"]:checked')) {
      if (optionNamePattern.test(input.getAttribute('name') || '')) return input.value || '';
    }

    for (const select of document.querySelectorAll('select')) {
      if (optionNamePattern.test(select.getAttribute('name') || '')) return select.value || '';
    }

    return '';
  }

  /**
   * 当前选中的是不是「含额外电池」那档（Plus）。
   *
   * 主判据：主题注入的 `data-np-kit-tier`（"base" / "bundle"）。这条链路
   *   snippets/neopine-kit-label.liquid (field:'tier') → variant-main-picker.liquid
   *   → <input data-np-kit-tier="…">
   * 与选项文案完全解耦，后台怎么改名都不受影响。
   *
   * 降级：模板没注入 data 属性时（旧模板 / 别的商品）退回按文案猜，只覆盖旧命名。
   */
  function isBundleSelected() {
    const marked = document.querySelector('[data-np-kit-tier]:checked');
    if (marked) return marked.getAttribute('data-np-kit-tier') === 'bundle';

    return /battery/i.test(selectedOptionValue(/kit/i));
  }

  /** 主题换变体时会整体替换 media-gallery，所以每次都要重新取 */
  function gallerySlideshow() {
    const gallery = document.querySelector('media-gallery');
    return gallery ? gallery.querySelector('slideshow-component') : null;
  }

  function gallerySlides(slideshow) {
    return Array.from(slideshow.querySelectorAll('slideshow-slide'));
  }

  /** 滚动方向：默认横向，容器高度可滚、宽度不可滚时按纵向算（移动端图集是纵向的） */
  function scrollAxisOf(element) {
    const scrollsVertically = element.scrollHeight > element.clientHeight + ALIGN_TOLERANCE;
    const scrollsHorizontally = element.scrollWidth > element.clientWidth + ALIGN_TOLERANCE;
    return scrollsVertically && !scrollsHorizontally ? 'y' : 'x';
  }

  /** 当前该停在第一张（null = 图集还没换成含套图的版本，继续等） */
  function desiredSlideIndex(slideshow) {
    const slides = gallerySlides(slideshow);
    if (!slides.length) return null;

    // 单机档：停在设备那张
    if (!isBundleSelected()) return 0;

    const index = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      return image ? BATTERY_SLIDE_RE.test(image.alt || '') : false;
    });

    return index >= 0 ? index : null;
  }

  /** 这张图此刻是不是真的停在可视区里（只看几何，不看主题内部状态） */
  function isSlideInPlace(slideshow, index) {
    const scroller = slideshow.refs ? slideshow.refs.scroller : null;
    const slide = gallerySlides(slideshow)[index];
    if (!scroller || !slide) return false;

    const viewport = scroller.getBoundingClientRect();
    if (!viewport.width || !viewport.height) return false;

    const slideBox = slide.getBoundingClientRect();
    const delta = scrollAxisOf(scroller) === 'y' ? slideBox.top - viewport.top : slideBox.left - viewport.left;

    return Math.abs(delta) <= ALIGN_TOLERANCE;
  }

  function revealSlide(slideshow, index) {
    const scroller = slideshow.refs ? slideshow.refs.scroller : null;
    const slide = gallerySlides(slideshow)[index];
    if (!scroller || !slide) return;

    // ① 先走主题自己的 API：分页点、计数器、aria 状态都会跟着同步
    if (typeof slideshow.select === 'function') {
      try {
        slideshow.select(index, undefined, { animate: false });
      } catch (error) {
        console.warn('[NEOPINE] slideshow.select failed:', error);
      }
    }

    if (isSlideInPlace(slideshow, index)) return;

    // ② 走到这里说明上面的调用没产生实际位移（组件还没初始化完 / 图片还没解码），
    //    直接用"当前矩形差"把滚动条推过去。用相对位移而不是绝对坐标，
    //    这样不依赖主题内部包裹层的偏移算法。
    const axis = scrollAxisOf(scroller);
    const viewport = scroller.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    const delta = axis === 'y' ? slideBox.top - viewport.top : slideBox.left - viewport.left;

    if (Math.abs(delta) <= ALIGN_TOLERANCE) return;

    if (axis === 'y') scroller.scrollTop += delta;
    else scroller.scrollLeft += delta;
  }

  function syncStop() {
    if (syncTimer) {
      window.clearInterval(syncTimer);
      syncTimer = null;
    }
    syncDeadline = 0;
  }

  function syncTick() {
    if (Date.now() > syncDeadline) {
      syncStop();
      return;
    }

    const slideshow = gallerySlideshow();
    if (!slideshow) return;

    const index = desiredSlideIndex(slideshow);
    if (index === null) return; // 含套图的那版图集还没换上来，下一轮再看

    if (!isSlideInPlace(slideshow, index)) revealSlide(slideshow, index);
  }

  /** 开始（或延长）一段落位窗口 */
  function syncStart() {
    syncDeadline = Date.now() + SYNC_WINDOW;
    if (!syncTimer) syncTimer = window.setInterval(syncTick, SYNC_STEP);
    syncTick();
  }

  function initKitMedia() {
    if (!document.querySelector('media-gallery')) return;

    // 主路径：主题换完图集后广播 variant:update（bubbles，能到 document）
    document.addEventListener('variant:update', syncStart);

    // 兜底路径：万一 variant:update 没来（请求失败等），点选动作本身也触发一次。
    // 此刻图集可能还是旧的，窗口内的轮询会等它换好再落位；重复触发只是延长窗口。
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!/kit/i.test(target.getAttribute('name') || '')) return;
      syncStart();
    });

    // 用户自己动手就收手：在图集里滑动、拖拽、点箭头或分页点都算
    const stopIfInGallery = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('media-gallery')) syncStop();
    };

    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      document.addEventListener(type, stopIfInGallery, { capture: true, passive: true });
    }

    // 首屏就落在带电池档位（URL 带 variant / option_values 进来）时也要处理
    syncStart();
  }

  function init() {
    // 两个特性互不牵连：其中一个抛错不能把另一个也带走
    for (const setup of [initHeroShrink, initKitMedia]) {
      try {
        setup();
      } catch (error) {
        console.error('[NEOPINE] init failed:', error);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
